<template>
  <div
    id="botModal"
    class="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4 hidden"
  >
    <div
      class="w-full max-w-md mx-auto bg-[#0C0E1B] border border-orange-500 rounded-2xl p-6 text-white shadow-2xl relative"
    >
      <button
        class="absolute top-3 right-4 text-gray-400 hover:text-white text-2xl font-bold z-10"
        @click="fecharModal"
      >
        &times;
      </button>

      <h2 class="text-xl font-semibold mb-6 text-center">
        {{
          etapa === 1
            ? t("modal.newQuota")
            : etapa === 2
              ? t("modal.confirmation")
              : t("modal.payment")
        }}
      </h2>

      <div v-if="etapa === 1">
        <div class="mb-4">
          <label class="block text-sm text-gray-400 mb-1">{{
            t("modal.choosePlan")
          }}</label>
          <select
            v-model="planoSelecionado"
            class="bg-[#10182C] text-white border border-[#2D3A5C] rounded-md w-full px-3 h-10"
          >
            <option v-for="p in planosOrdenados" :key="p.name" :value="p">
              {{ p.name }}
            </option>
          </select>
        </div>

        <div v-if="planoSelecionado" class="mb-6">
          <p class="text-sm text-gray-400 mb-1">{{ t("modal.quotaValue") }}</p>
          <p class="text-base font-bold text-white mb-2">
            R$ {{ planoSelecionado.amount }}
          </p>
        </div>

        <button
          class="bg-orange-500 hover:bg-orange-600 text-white font-semibold w-full py-2 rounded-md transition"
          @click="etapa = 2"
        >
          {{ t("modal.continue") }}
        </button>
      </div>

      <div v-else-if="etapa === 2">
        <p class="text-sm text-gray-300 mb-6 leading-relaxed">
          {{
            t("modal.confirmPurchase", {
              plan: planoSelecionado?.name,
              amount: planoSelecionado?.amount || 0,
            })
          }}
        </p>

        <div class="mb-6 flex items-start gap-2">
          <input
            id="aceite"
            v-model="aceitouTermos"
            type="checkbox"
            class="mt-1"
          />
          <label for="aceite" class="text-sm text-gray-300 leading-tight">
            {{ t("modal.acceptTerms") }}
            <a
              href="/accept-terms"
              target="_blank"
              class="text-orange-400 underline"
              >{{ t("modal.termsOfUse") }}</a
            >.
          </label>
        </div>

        <div class="flex justify-between gap-4">
          <button
            class="bg-gray-600 hover:bg-gray-700 text-white font-semibold w-1/2 py-2 rounded-md transition"
            @click="etapa = 1"
          >
            {{ t("modal.back") }}
          </button>

          <button
            class="bg-orange-500 hover:bg-orange-600 text-white font-semibold w-1/2 py-2 rounded-md transition disabled:opacity-50"
            :disabled="!aceitouTermos || isLoading"
            @click="gerarDeposito"
          >
            <span v-if="!isLoading">{{ t("modal.confirm") }}</span>
            <span v-else>{{ t("modal.loadingPayment") }}...</span>
          </button>
        </div>

        <p v-if="mensagem" class="mt-3 text-center text-sm text-red-400">
          {{ mensagem }}
        </p>
      </div>

      <div v-else class="space-y-5">
        <div class="text-sm text-gray-300">
          <p>
            {{
              t("modal.paymentValue", { amount: planoSelecionado?.amount || 0 })
            }}
          </p>
          <p class="opacity-80">{{ t("modal.paymentInstruction") }}</p>
        </div>

        <div class="flex flex-col items-center gap-4">
          <img
            v-if="pixQRCode"
            :src="pixQRCode"
            alt="QR Code PIX"
            class="w-48 h-48"
          />
          <div v-else class="text-sm text-gray-400">
            {{ t("modal.loadingQr") }}
          </div>
        </div>

        <div>
          <label class="text-gray-400 text-sm mb-1 block">{{
            t("modal.pixLine")
          }}</label>
          <div class="flex items-center">
            <input
              v-model="pixCopy"
              readonly
              class="w-full px-4 py-2 rounded-md bg-[#10182C] border border-[#2D3A5C] text-white mr-2"
            />
            <button
              class="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-md font-semibold"
              @click="copyPix"
            >
              {{ t("modal.copy") }}
            </button>
          </div>
        </div>

        <div class="flex gap-3">
          <button
            class="bg-gray-600 hover:bg-gray-700 text-white font-semibold w-1/2 py-2 rounded-md transition"
            @click="etapa = 2"
          >
            {{ t("modal.back") }}
          </button>
          <button
            class="bg-orange-500 hover:bg-orange-600 text-white font-semibold w-1/2 py-2 rounded-md transition"
            @click="concluir"
          >
            {{ t("modal.finish") }}
          </button>
        </div>

        <p class="text-center text-sm text-yellow-400">
          {{ t("modal.alert_message") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import QRCode from "qrcode";
import { useToast } from "vuestic-ui";
import { create } from "@bufbuild/protobuf";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useDepositStore } from "@/stores/deposit";
import { useI18n } from "vue-i18n";
import { planService } from "@/services/plan.service";
import { ListPlansRequestSchema } from "@/gen/api/v1/plan_pb";
import { Deposit, DepositSchema } from "@/gen/api/v1/api_pb";

const { t } = useI18n();

const { init } = useToast();

// stores
const userStore = useUserStore();
const authStore = useAuthStore();
const depositStore = useDepositStore();

// UI state
const planos = ref<{ duration: number; amount: number; name: string }[]>([]);
const planoSelecionado = ref<{
  duration: number;
  amount: number;
  name: string;
} | null>(null);
const etapa = ref(1);
const aceitouTermos = ref(false);
const isLoading = ref(false);
const mensagem = ref("");

// PIX state
const pixPayload = ref("");
const pixQRCode = ref("");
const pixCopy = ref("");
const pixId = ref<number | string>("");
const pixStatus = ref("");
const pixExternalId = ref("");
const pixFee = ref(0);
async function gerarDeposito() {
  if (!planoSelecionado.value) return;
  if (!aceitouTermos.value) return;

  const user = authStore.user || userStore.user;
  if (!user) {
    mensagem.value = "Usuário não autenticado.";
    return;
  }

  isLoading.value = true;
  mensagem.value = "";
  try {
    const req = create(DepositSchema, {
      id: 1, // se o backend ignorar/gerar, mantenha; caso contrário, remova conforme contrato
      userId: user.id,
      amount: planoSelecionado.value.amount,
    });

    const resp = await depositStore.createDeposit(req);
    if (!resp) throw new Error("Não foi possível criar o depósito.");

    interface FullDeposit extends Deposit {
      pixQrCode: string;
      status: string;
      externalId: string;
      fee: number;
    }

    const dp = resp as unknown as FullDeposit;

    pixPayload.value = dp.pixQrCode;
    pixCopy.value = dp.pixQrCode;
    pixId.value = dp.id;
    pixStatus.value = dp.status;
    pixExternalId.value = dp.externalId;
    pixFee.value = dp.fee;

    // Gera imagem do QR Code
    pixQRCode.value = await QRCode.toDataURL(dp.pixQrCode);

    etapa.value = 3;
    init({ message: "QRCode para pagamento gerado!", color: "success" });
  } catch (e: any) {
    console.error(e);
    mensagem.value = e?.message || "Erro ao gerar qrcode.";
    init({ message: mensagem.value, color: "danger" });
  } finally {
    isLoading.value = false;
  }
}

function concluir() {
  fecharModal();
}

function copyPix() {
  if (!pixCopy.value) return;
  navigator.clipboard.writeText(pixCopy.value);
  init({ message: "Linha PIX copiada!", color: "success" });
}

function resetarPix() {
  pixPayload.value = "";
  pixQRCode.value = "";
  pixCopy.value = "";
  pixId.value = "";
  pixStatus.value = "";
  pixExternalId.value = "";
  pixFee.value = 0;
}

function fecharModal() {
  const modal = document.getElementById("botModal");
  if (modal) {
    modal.classList.add("hidden");
    etapa.value = 1;
    aceitouTermos.value = false;
    isLoading.value = false;
    mensagem.value = "";
    resetarPix();
  }
}

onMounted(async () => {
  const request = create(ListPlansRequestSchema, { page: 1, pageSize: 1000 });
  const response = await planService.listPlans(request);

  const raw = response.plans.map((p: any) => ({
    duration: p.timeStake,
    amount: p.amount,
    name: p.name,
  }));

  raw.sort((a, b) => normalizeAmount(b.amount) - normalizeAmount(a.amount));
  planos.value = raw;

  planoSelecionado.value = planos.value[0] || null;
});
const normalizeAmount = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "string") {
    const s = v
      .replace(/\s/g, "")
      .replace(/[R$\u00A0]/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const planosOrdenados = computed(() =>
  [...planos.value].sort(
    (a, b) => normalizeAmount(b.amount) - normalizeAmount(a.amount),
  ),
);
</script>
